import cn from 'classnames';
import { range } from 'lodash';
import { Fragment, ReactNode, useState } from 'react';

import { ReactComponent as CaretIcon } from 'assets/icons/CaretCircleDownOrangeFilled.svg';
import { Divider } from 'shared/Divider';
import { LoadingSkeleton } from 'shared/index';
import { TableDataMap } from 'types/components/table.types';

import styles from './TableBody.module.scss';

type Props<T extends NonNullable<object>> = {
  values: T[] | undefined | null;
  testId?: string;
  isLoading?: boolean;
  loadingCount?: number;
  ExpandedElement?: (item: T) => ReactNode;
  RowActionElement?: React.ReactNode;
  onRowAction?: (rowItem: T) => void;
  transformValueMapper: TableDataMap<T>;
};

export const TableBody = <T extends NonNullable<object>>({
  values,
  ExpandedElement,
  RowActionElement,
  transformValueMapper,
  onRowAction,
  isLoading,
  loadingCount = 0,
  testId = 'table-body',
}: Props<T>) => {
  const [isExpanded, setIsExpanded] = useState<null | number>(null);
  const LOADING_SKELETON_ROWS = 3;

  if (isLoading) {
    return (
      <tbody>
        {range(LOADING_SKELETON_ROWS).map((rowNumber, rowIndex) => (
          <tr data-testid={`${testId}-loading-row`} key={rowNumber + rowIndex}>
            {range(loadingCount).map((cellNumber, cellIndex) => (
              <td key={cellNumber + cellIndex}>
                <LoadingSkeleton className={styles.loadingCell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }

  return (
    <tbody data-testid={testId}>
      <tr className={styles.dividerRow}>
        <td>
          <Divider className={styles.divider} />
        </td>
      </tr>
      {values?.map((v, i) => (
        <Fragment key={i.toString()}>
          <tr data-testid={`${testId}-row`}>
            {Object.keys(v)
              .filter(key => key !== 'id')
              .map((key, index) => {
                const cellValue = transformValueMapper?.[key as keyof T]?.(v);

                return (
                  cellValue && (
                    <td key={i + index + key} data-testid={`${testId}-cell-${index}`}>
                      <div>
                        {typeof cellValue === 'string' || typeof cellValue === 'number' ? (
                          <span>{cellValue}</span>
                        ) : (
                          cellValue
                        )}
                      </div>
                    </td>
                  )
                );
              })}
            {RowActionElement && (
              <td onClick={() => onRowAction?.(v)} data-testid={`${testId}-action-cell`}>
                {RowActionElement}
              </td>
            )}
            {!!ExpandedElement && (
              <td data-testid={`${testId}-expanding-cell`}>
                <CaretIcon
                  className={cn(isExpanded === i && styles.openCaret)}
                  onClick={() => setIsExpanded(isExpanded === i ? null : i)}
                />
              </td>
            )}
          </tr>
          {isExpanded === i && (
            <tr data-testid={`${testId}-expanded-row`}>
              <td colSpan={values.length}>{ExpandedElement?.(v)}</td>
            </tr>
          )}
          <tr className={styles.dividerRow}>
            <td>
              <Divider className={styles.divider} />
            </td>
          </tr>
        </Fragment>
      ))}
    </tbody>
  );
};
